import { useEffect, useRef, useState, type ReactElement } from 'react';
import { trackFailed } from '../../../../../../shared-web/track';
import { ApiError } from '../../../auth/api-client';
import { apiErrorCode, apiFailureReason, emberApi, isAuthRequired, wordsRefused, type TopicRoomPreview, type TopicSeat } from '../../api';
import { useChatsLive, useChatsSnapshot } from '../../chats-store';
import { EmberMascot } from '../../components/EmberMascot';
import { Link } from '../../components/Link';
import { PageFailure } from '../../components/PageFailure';
import { PageLoading } from '../../components/PageLoading';
import { PresenceAvatar } from '../../components/PresenceAvatar';
import { notifyKnocksChanged } from '../../components/use-knocks';
import { goBack, navigate } from '../../router';
import { useRequireAuth } from '../../use-require-auth';
import { fitHeight } from './friend-parts';
import { useFriendsConfig } from '../../client-config';
import { FLAME, OPENS_AT, PENCIL, RoomsFull, SeatToCome, TopicBar, countLine, seatInk } from './topic-shared';

/** Where the page stands: the look (asking or invited), the quick look running, and how it ended. */
type Phase = 'look' | 'checking' | 'joining' | 'in' | 'no_fit' | 'full' | 'gone' | 'left';

/**
 * A topic room someone else put up (/friends/rooms/:roomId; 2026-10-26 founder, rooms by topic; mock RoomJoin,
 * RoomJoinNo, RoomReady): the topic, who is in with the line on each seat, how much space is left, and the seat this
 * person would take — the line Ember drafted from their conversation, theirs to change. Two ways in: asked on the page,
 * Ember takes a quick look at whether it fits them (the answer is yes or "not quite right for you yet", never why);
 * invited by Ember in their own conversation (`?invited=1`, the card under its reply), straight in. In an open room they
 * go to the thread and read what is said from then on; in one still waiting for people they wait with the others until
 * the third is in.
 */
export function TopicRoomPage({ roomId, invited: invitedAtFirst }: { roomId: string; invited: boolean }): ReactElement {
  // Meet people's served limits and sentences (2026-11-01 Riley, client-config.ts): redrawn when the config changes
  const served = useFriendsConfig();
  const path = `/friends/rooms/${encodeURIComponent(roomId)}${invitedAtFirst ? '?invited=1' : ''}`;
  const session = useRequireAuth(path);
  const [preview, setPreview] = useState<TopicRoomPreview | null>(null);
  const [failed, setFailed] = useState<'' | 'not_eligible' | 'load_failed'>('');
  // Invited until the edge says the invitation is not this account's (not_invited): then they may ask instead.
  const [invited, setInvited] = useState(invitedAtFirst);
  const [phase, setPhase] = useState<Phase>('look');
  const [line, setLine] = useState('');
  const [editing, setEditing] = useState(false);
  const [notice, setNotice] = useState('');
  const [present, setPresent] = useState(0);   // how many are in, this person with them, once they are in a room still waiting

  const toAuth = (): void => navigate(`/auth?returnTo=${encodeURIComponent(path)}`, { replace: true });
  // Not a room they could come into: one they are already in (the card under Ember's reply stays after they took it)
  // opens as their room; any other is not taking people.
  const notOpen = (): void => {
    emberApi.room(roomId)
      .then(() => navigate(`/rooms/${encodeURIComponent(roomId)}`, { replace: true }))
      .catch((again: unknown) => {
        if (!(again instanceof ApiError && again.status === 404)) trackFailed('topic_room_load', 'room_check_failed', { error_detail: apiFailureReason(again), match_id: roomId });
        setPhase('gone');
      });
  };

  useEffect(() => {
    if (session !== 'authenticated') return;
    emberApi.topicRoom(roomId)
      // All their places held: the 3-of-3 state up front, not an Ask to join that fails after the tap (2026-10-26).
      .then((p) => { setPreview(p); setLine(p.line); if (p.full) setPhase('full'); })
      .catch((err: unknown) => {
        if (isAuthRequired(err)) { toAuth(); return; }
        if (apiErrorCode(err) === 'not_eligible') { setFailed('not_eligible'); return; }
        if (err instanceof ApiError && err.status === 404) { notOpen(); return; }
        trackFailed('topic_room_load', 'load_failed', { error_detail: apiFailureReason(err), match_id: roomId });
        setFailed('load_failed');
      });
  }, [session, roomId]);

  // Once they are in and it waits for people, the page follows the room on every read of their Chats and when the tab
  // comes back (2026-10-31 founder: a page follows the one change signal; as the App's): the room as its member reads it
  // (the look is a 404 to one in it) — open, the thread, as when their own step opens it; still waiting, how many are in.
  // Deciding is left as it is: its read may draft their line with a model, and the App does not follow it either.
  const shownRef = useRef<Phase | null>(null);
  shownRef.current = preview ? phase : null;
  const reread = (): void => {
    if (shownRef.current === 'in') {
      emberApi.room(roomId)
        .then((r) => {
          if (r.room.status === 'open') navigate(`/talk?mode=chat&room=${encodeURIComponent(roomId)}`, { replace: true });
          else setPresent((r.room.yeses ?? []).length + 1);
        })
        .catch(() => { /* the page stays as it was */ });
    }
  };
  useEffect(() => {
    const again = (): void => { if (document.visibilityState === 'visible') reread(); };
    document.addEventListener('visibilitychange', again);
    return () => document.removeEventListener('visibilitychange', again);
  }, [roomId]);
  useChatsLive(session === 'authenticated');
  const chats = useChatsSnapshot().data;
  useEffect(() => { if (chats) reread(); }, [chats]);

  if (failed === 'not_eligible') return <PageFailure title="This isn’t available right now." logState="not_eligible" />;
  if (failed) return <PageFailure title="Couldn’t open this page" logState="load_failed" retry />;

  const refusal = (title: string, body: string, state: string): ReactElement => (
    <main className="topic-page">
      <TopicBar to="/friends" />
      <div className="topic-state" data-log-seen="topic_room_refused" data-log-state={state} data-log-match-id={roomId}>
        <span className="topic-state-art" aria-hidden><span className="topic-state-flame">{FLAME}</span></span>
        <h1 className="topic-h1">{title}</h1>
        <p className="topic-state-body">{body}</p>
      </div>
      <div className="topic-foot">
        <Link to="/friends/rooms/new" className="topic-btn" data-log="topic_room_find" data-log-state={state} data-log-match-id={roomId}>Find another room</Link>
        <Link to="/talk?mode=chat" className="topic-btn is-quiet" data-log="topic_room_to_chat" data-log-state={state} data-log-match-id={roomId}>Back to chat</Link>
      </div>
    </main>
  );
  if (phase === 'gone') return refusal('This room isn’t taking people right now', 'It may be full, or it has closed. There are more to pick from!', 'gone');
  if (phase === 'left') return refusal('You left this room earlier', 'A room you’ve left stays closed to you. There are more to pick from!', 'left_before');
  if (phase === 'full') {
    return (
      <main className="topic-page">
        <TopicBar to="/friends" />
        <RoomsFull topic={preview?.room.name} />
      </main>
    );
  }
  if (!preview) return <PageLoading />;

  const { room } = preview;
  const edited = line.trim() !== preview.line.trim();

  if (phase === 'no_fit') {
    return (
      <main className="topic-page">
        <TopicBar to="/friends" />
        <div className="topic-state" data-log-seen="topic_room_no_fit" data-log-match-id={roomId}>
          {/* Ember thinking it over (2026-10-27 Riley: the empty seat read as a picture that failed to load) — the face the
              Chats list's "Ember is looking" row wears */}
          <span className="topic-state-art" aria-hidden>
            <span className="topic-state-ember"><EmberMascot face="thinking" still interactive={false} className="topic-state-ember-face" /></span>
          </span>
          <span className="topic-kicker is-accent">hmm, not this one</span>
          <h1 className="topic-h1">Let’s find you a better room</h1>
          <p className="topic-state-body" dir="auto">I don’t think “{room.name}” is quite right for you yet. Put up a topic of your own, or tell me what’s on your mind.</p>
        </div>
        <div className="topic-foot">
          <Link to="/friends/rooms/new" className="topic-btn" data-log="topic_room_no_fit" data-log-state="pick" data-log-match-id={roomId}>Pick a topic</Link>
          <Link to="/talk?mode=chat" className="topic-btn is-quiet" data-log="topic_room_no_fit" data-log-state="chat" data-log-match-id={roomId}>Back to chat</Link>
        </div>
      </main>
    );
  }

  if (phase === 'in') {
    // In a room still waiting for people (RoomReady's look): it opens with the third, and Ember says so.
    return (
      <main className="topic-page" data-log-seen="topic_room_in" data-log-state="forming" data-log-match-id={roomId}>
        <TopicBar to="/friends"><span className="topic-bar-label">Waiting for people</span></TopicBar>
        <div className="topic-body">
          <section className="topic-band" aria-hidden>
            <span className="topic-kicker is-room">you’re in!</span>
            <span className="topic-band-seats">
              {room.people.slice(0, 2).map((p) => <span key={p.name} className="topic-band-seat"><PresenceAvatar avatarKey={p.avatar} size={72} /></span>)}
              <span className="topic-band-seat"><SeatToCome size={72} /></span>
            </span>
          </section>
          <div className="topic-head is-center">
            <h1 className="topic-h1 is-room" dir="auto">{room.name}</h1>
            <span className="topic-sub">{present} of you so far · it opens when {OPENS_AT} are in</span>
          </div>
          <People people={room.people} you={{ seat: preview.seat, line: line.trim() }} />
          <p className="topic-small">I’ll let you know the moment it opens. Drop in whenever after that.</p>
        </div>
        <div className="topic-foot">
          <Link to="/friends" className="topic-btn" data-log="topic_room_in_rooms" data-log-match-id={roomId}>See my rooms</Link>
          <Link to="/talk?mode=chat" className="topic-btn is-quiet" data-log="topic_room_to_chat" data-log-state="in" data-log-match-id={roomId}>Back to chat</Link>
        </div>
      </main>
    );
  }

  /** In: an open room is the thread; one still waiting is the "you're in" above. */
  const landed = (status: string, count: number): void => {
    notifyKnocksChanged();
    if (status === 'open') { navigate(`/talk?mode=chat&room=${encodeURIComponent(roomId)}`, { replace: true }); return; }
    setPresent(count);
    setPhase('in');
  };
  const refused = (err: unknown, name: string): void => {
    if (isAuthRequired(err)) { toAuth(); return; }
    const code = apiErrorCode(err);
    const words = wordsRefused(err, { intro: 'Your line' });
    trackFailed(name, apiFailureReason(err), { match_id: roomId });
    setPhase('look');
    if (code === 'not_invited') { setInvited(false); setNotice('That invitation isn’t on this account. You can still ask to join.'); }
    else if (code === 'rooms_full') setPhase('full');
    else if (code === 'room_full' || code === 'room_not_available' || code === 'not_found') setPhase('gone');
    else if (code === 'left_before') setPhase('left');
    else if (code === 'not_eligible') setFailed('not_eligible');
    else if (code === 'unavailable') setNotice('I couldn’t take a look just now. Try again in a moment.');
    // The line on their seat did not pass review: open to change, with why.
    else if (words) { setEditing(true); setNotice(words.message); }
    else setNotice('That didn’t go through. Try again.');
  };

  const hopIn = async (): Promise<void> => {
    setNotice(''); setEditing(false); setPhase('joining');
    try {
      const out = await emberApi.joinTopicRoom(roomId, line.trim(), edited);
      landed(out.room_status, out.present ?? room.present + 1);
    } catch (err) { refused(err, 'topic_room_join'); }
  };
  const ask = async (): Promise<void> => {
    setNotice(''); setEditing(false); setPhase('checking');
    try {
      const out = await emberApi.applyToTopicRoom(roomId, line.trim(), edited);
      if (!out.fit) { setPhase('no_fit'); return; }
      landed(out.room_status, room.present + 1);
    } catch (err) { refused(err, 'topic_room_apply'); }
  };

  const working = phase === 'checking' || phase === 'joining';
  return (
    <main className="topic-page" data-log-seen="topic_room" data-log-state={invited ? 'invited' : 'ask'} data-log-match-id={roomId}>
      <TopicBar to="/friends"><span className="topic-bar-label">{room.status === 'open' ? 'An open room' : 'Waiting for people'}</span></TopicBar>
      <div className="topic-body">
        <div className="topic-head is-center">
          <span className="topic-kicker is-room">come say hi?</span>
          <h1 className="topic-h1 is-room" dir="auto">{room.name}</h1>
          <span className="topic-sub">{room.status === 'open' ? `${countLine(room)} · drop in whenever` : countLine(room)}</span>
        </div>

        {room.people.length > 0 && <People people={room.people} />}

        <div className="topic-you">
          <span className="topic-you-head">Here’s you</span>
          {/* Editing in place (2026-10-27 Riley: a bordered box inside the bordered card, Done squeezed beside it): the field is
              a soft patch of the card, the whole width under the name, Done under it */}
          <section className={`topic-you-card${editing ? ' is-editing' : ''}`}>
            <PresenceAvatar avatarKey={preview.seat?.avatar ?? ''} size={54} />
            <span className="topic-person-text">
              <span className="topic-person-name" dir="auto">{preview.seat?.name}</span>
              {editing ? (
                <>
                  <textarea className="topic-seat-field is-inline" value={line} maxLength={served.limits.room_intro_max_chars} rows={2} ref={fitHeight} onInput={(e) => fitHeight(e.currentTarget)} autoFocus
                    placeholder={`A line about you and “${room.topic}”`} aria-label="The line on your seat" onChange={(e) => setLine(e.target.value)} />
                  <button type="button" className="topic-link is-under" onClick={() => setEditing(false)}>Done</button>
                </>
              ) : (
                <span className={`topic-person-line${line.trim() ? '' : ' is-empty'}`} dir="auto">{line.trim() || 'Add a line about you, if you like.'}</span>
              )}
            </span>
            {!editing && <button type="button" className="topic-pencil" aria-label="Edit my line" disabled={working} data-log="topic_room_line_edit" data-log-match-id={roomId} onClick={() => setEditing(true)}>{PENCIL}</button>}
          </section>
          <span className="topic-note is-center">{preview.line ? 'I drafted your line from our chats. Change anything.' : 'Your nickname in rooms, never your real name.'}</span>
        </div>
      </div>

      <div className="topic-foot">
        {/* Into an open room, asking or invited, a newcomer reads from their arrival on — said on both ways in (2026-10-27
            walk-through: two who asked in landed in what looked like an empty room). */}
        <p className="topic-small">{[!invited ? 'I’ll take a quick look to make sure it’s a good fit.' : '', room.status === 'open' ? 'You’ll see what’s said from now on.' : invited ? (room.present + 1 >= OPENS_AT ? 'You’d be the third, so it opens when you hop in.' : 'I’ll let you know when it opens.') : ''].filter(Boolean).join(' ')}</p>
        {notice && <p className="topic-notice" role="alert">{notice}</p>}
        {phase === 'checking' ? (
          <div className="topic-btn is-looking" role="status">
            Taking a quick look
            <span className="topic-looking-dots" aria-hidden><i /><i /><i /></span>
          </div>
        ) : invited ? (
          <button type="button" className="topic-btn" disabled={working} data-log="topic_room_join" data-log-state={edited ? 'edited' : 'drafted'} data-log-match-id={roomId} onClick={() => { void hopIn(); }}>Hop in</button>
        ) : (
          <button type="button" className="topic-btn" disabled={working} data-log="topic_room_apply" data-log-state={edited ? 'edited' : 'drafted'} data-log-match-id={roomId} onClick={() => { void ask(); }}>Ask to join</button>
        )}
        <button type="button" className="topic-btn is-quiet" data-log="topic_room_not_now" data-log-match-id={roomId} onClick={() => goBack(null, '/friends')}>Not now</button>
      </div>
    </main>
  );
}

/** Who is in, each with the line on their seat; with `you`, this person last (their seat and line, once they are in). */
function People({ people, you }: { people: TopicRoomPreview['room']['people']; you?: { seat?: TopicSeat; line: string } }): ReactElement {
  return (
    <ul className="topic-people" aria-label="Who’s in">
      {people.map((p, i) => (
        <li key={p.name} className="topic-person">
          <PresenceAvatar avatarKey={p.avatar} size={36} />
          <span className="topic-person-text">
            <span className={`topic-person-name ${seatInk(i)}`} dir="auto">{p.name}</span>
            {p.intro && <span className="topic-person-line" dir="auto">{p.intro}</span>}
          </span>
        </li>
      ))}
      {you !== undefined && (
        <li className="topic-person">
          <PresenceAvatar avatarKey={you.seat?.avatar ?? ''} size={36} />
          <span className="topic-person-text">
            <span className="topic-person-name" dir="auto">{you.seat?.name} (you)</span>
            {you.line && <span className="topic-person-line" dir="auto">{you.line}</span>}
          </span>
        </li>
      )}
    </ul>
  );
}
